import { useEffect, useRef, useState } from "preact/hooks";
import { Brand, Mark } from "./brand";
import commerceArtwork from "./assets/hero/commerce-running-shoe.webp";
import supportArtwork from "./assets/hero/services-rehabilitation.webp";
import { HeroDemo } from "./hero-demo";
import {
  ArrowRightIcon,
  ArrowUpRightIcon,
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  CodeIcon,
  GlobeIcon,
  HumanIcon,
  MenuIcon,
  MessageIcon,
  SearchIcon,
  ShieldIcon,
  SlidersIcon,
  SparkIcon,
} from "./icons";

const contactHref =
  "mailto:kontakt@chatland.pl?subject=Rozmowa%20o%20pilotażu%20Chatland";

const supportPoints = [
  "Odpowiada na pytania o ofertę, zasady i usługi",
  "Pozyskuje kontakt poza godzinami pracy",
  "Przekazuje trudną rozmowę wraz z kontekstem",
];

const commercePoints = [
  "Rozumie zastosowanie, rozmiar i budżet",
  "Filtruje warianty, cenę i dostępność",
  "Prowadzi klienta do właściwego produktu",
];

const trustSteps = [
  {
    number: "01",
    icon: <SearchIcon />,
    title: "Najpierw sprawdza",
    text: "Przeszukuje zatwierdzone strony, dokumenty i katalog. Każdy fakt ma źródło.",
  },
  {
    number: "02",
    icon: <ShieldIcon />,
    title: "Potem odpowiada",
    text: "Krytyczne dane — cena, dostępność czy zasady — przechodzą dodatkową kontrolę.",
  },
  {
    number: "03",
    icon: <HumanIcon />,
    title: "Wie, kiedy oddać rozmowę",
    text: "Gdy brakuje pewności lub potrzebne są dane klienta, przekazuje pełny kontekst zespołowi.",
  },
];

const workflowStages = [
  {
    number: "01",
    phase: "Wiedza",
    icon: <GlobeIcon />,
    title: "Podłączamy to, co firma już wie.",
    text: "Strona, dokumenty, FAQ albo katalog stają się uporządkowanym i wersjonowanym źródłem odpowiedzi.",
    proof: "Źródła widoczne przed publikacją",
  },
  {
    number: "02",
    phase: "Zasady",
    icon: <SlidersIcon />,
    title: "Ustalamy granice rozmowy.",
    text: "Definiujemy ton, godziny wsparcia, krytyczne fakty oraz moment, w którym rozmowę przejmuje człowiek.",
    proof: "Każda reguła wymaga zatwierdzenia",
  },
  {
    number: "03",
    phase: "Start",
    icon: <CodeIcon />,
    title: "Publikujemy jedną linijką kodu.",
    text: "Po teście domeny i wiedzy widget trafia na wybrane podstrony bez przebudowy istniejącego serwisu.",
    proof: "Weryfikacja, telemetria i rollback",
  },
];

const faqs = [
  {
    question: "Czy Chatland wymyśla odpowiedzi?",
    answer:
      "Chatland odpowiada na podstawie zatwierdzonych źródeł firmy. Gdy nie ma wystarczających danych, dopytuje, jasno komunikuje brak pewności albo przekazuje rozmowę człowiekowi — zamiast zgadywać.",
  },
  {
    question: "Czy poradzi sobie z dużym katalogiem produktów?",
    answer:
      "Tak. Architektura wyszukuje tylko najbardziej trafne produkty, a następnie stosuje twarde filtry, między innymi rozmiaru, dostępności, zastosowania i budżetu. Nie wysyłamy całego katalogu do modelu w każdej rozmowie.",
  },
  {
    question: "Ile trwa instalacja?",
    answer:
      "Podstawowa instalacja sprowadza się do jednej linijki kodu. Panel prowadzi przez weryfikację domeny, podłączenie wiedzy, publikację i test działania. Dla pierwszych partnerów konfigurację przeprowadzamy wspólnie.",
  },
  {
    question: "Czy wygląd asystenta można dopasować do strony?",
    answer:
      "Tak. Klient może dobrać kolor akcji, tekst powitania i podstawowe elementy prezentacji. Konstrukcja, dostępność i stany bezpieczeństwa pozostają kontrolowane przez Chatland.",
  },
  {
    question: "Co dzieje się, gdy klient chce porozmawiać z człowiekiem?",
    answer:
      "Rozmowa trafia do skrzynki zespołu z historią, rozpoznaną potrzebą i powodem przekazania. Konsultant może ją przejąć bez proszenia klienta o powtarzanie całego problemu.",
  },
  {
    question: "Jak chronione są dane i wiedza mojej firmy?",
    answer:
      "Każda firma działa w odseparowanym środowisku z kontrolą ról, dozwolonych domen i czasu przechowywania rozmów. Asystent korzysta wyłącznie ze źródeł przypisanych do właściwej strony, a działania administracyjne pozostawiają audyt.",
  },
  {
    question: "Czy mogę szybko wyłączyć asystenta?",
    answer:
      "Tak. Publikacja ma kontrolowany status, wersjonowany widget i przygotowany rollback. Asystenta można wyłączyć bez usuwania wiedzy oraz historii potrzebnej zespołowi do dalszej obsługi.",
  },
];

export function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mobileOpen) return;
    const focusable = Array.from(
      mobileMenuRef.current?.querySelectorAll<HTMLElement>(
        "a[href], button:not([disabled])",
      ) ?? [],
    );
    focusable[0]?.focus();

    const keepFocusInside = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
        mobileMenuButtonRef.current?.focus();
        return;
      }
      if (event.key !== "Tab" || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keepFocusInside);
    document.body.classList.add("menu-open");
    return () => {
      document.removeEventListener("keydown", keepFocusInside);
      document.body.classList.remove("menu-open");
    };
  }, [mobileOpen]);

  const closeMenu = () => setMobileOpen(false);

  return (
    <div class="site-shell">
      <a class="skip-link" href="#main">
        Przejdź do treści
      </a>

      <header class="site-header">
        <div class="container site-header__inner">
          <a
            class="site-logo"
            href="#top"
            aria-label="Chatland — strona główna"
          >
            <Brand wordmarkOnly />
          </a>

          <nav class="desktop-nav" aria-label="Główna nawigacja">
            <a href="#produkt">Produkt</a>
            <a href="#zaufanie">Trust Layer</a>
            <a href="#wdrozenie">Jak to działa</a>
            <a href="#faq">FAQ</a>
          </nav>

          <div class="site-header__actions">
            <a class="text-link" href="https://panel.chatland.pl/login">
              Zaloguj się
            </a>
            <a class="button button--header" href={contactHref}>
              Umów rozmowę <ArrowUpRightIcon />
            </a>
          </div>

          <button
            ref={mobileMenuButtonRef}
            class="mobile-menu-button"
            type="button"
            aria-label={mobileOpen ? "Zamknij menu" : "Otwórz menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMobileOpen((value) => !value)}
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>

        <div
          ref={mobileMenuRef}
          id="mobile-navigation"
          class={`mobile-nav${mobileOpen ? " mobile-nav--open" : ""}`}
          aria-hidden={!mobileOpen}
          inert={!mobileOpen}
        >
          <nav aria-label="Mobilna nawigacja">
            <a onClick={closeMenu} href="#produkt">
              Produkt <ArrowRightIcon />
            </a>
            <a onClick={closeMenu} href="#zaufanie">
              Trust Layer <ArrowRightIcon />
            </a>
            <a onClick={closeMenu} href="#wdrozenie">
              Jak to działa <ArrowRightIcon />
            </a>
            <a onClick={closeMenu} href="#faq">
              FAQ <ArrowRightIcon />
            </a>
          </nav>
          <a class="button" href={contactHref}>
            Umów rozmowę <ArrowUpRightIcon />
          </a>
          <a class="mobile-login" href="https://panel.chatland.pl/login">
            Zaloguj się do panelu
          </a>
        </div>
      </header>

      <main id="main">
        <section class="hero" id="top">
          <div class="hero__grid" aria-hidden="true" />
          <div class="container hero__layout">
            <div class="hero__copy">
              <div class="eyebrow hero__eyebrow">
                <span class="status-dot" />
                Asystent AI dla stron i sklepów
              </div>
              <h1>
                Każda rozmowa
                <br />
                <span>prowadzi dalej.</span>
              </h1>
              <p class="hero__lead">
                Chatland zna Twoją firmę, pomaga klientom znaleźć odpowiedź lub
                produkt i wie, kiedy przekazać rozmowę człowiekowi.
              </p>
              <div class="hero__actions">
                <a class="button button--large" href={contactHref}>
                  Porozmawiajmy o pilotażu <ArrowUpRightIcon />
                </a>
                <a class="button button--ghost" href="#produkt">
                  Zobacz, jak działa <ArrowRightIcon />
                </a>
              </div>
              <div
                class="hero__assurances"
                aria-label="Najważniejsze cechy wdrożenia"
              >
                <span>
                  <CheckIcon /> Odpowiedzi z Twoich danych
                </span>
                <span>
                  <CheckIcon /> Jedna linijka kodu
                </span>
                <span>
                  <CheckIcon /> Człowiek zawsze dostępny
                </span>
              </div>
            </div>

            <HeroDemo />
          </div>
        </section>

        <section class="statement-section section">
          <div class="container statement">
            <p class="section-index">01 / Produkt</p>
            <div class="statement__copy">
              <h2>
                Nie kolejny chatbot.
                <br />
                Nowa warstwa Twojej strony.
              </h2>
              <p>
                Zamiast kazać klientowi szukać, Chatland prowadzi rozmowę.
                Rozumie kontekst, korzysta z aktualnej wiedzy i zamienia pytanie
                w konkretny następny krok.
              </p>
            </div>
          </div>
        </section>

        <section class="product-lines section" id="produkt">
          <div class="container">
            <div class="section-heading">
              <div>
                <p class="eyebrow">Jeden rdzeń · dwa zastosowania</p>
                <h2>
                  Pomoc, która rozumie.
                  <br />
                  Sprzedaż, która nie naciska.
                </h2>
              </div>
              <p>
                Ten sam inteligentny asystent dopasowuje się do celu strony.
                Może działać jako żywe FAQ, doradca zakupowy albo łączyć oba
                tryby.
              </p>
            </div>

            <div class="product-stories">
              <article class="product-story product-story--support">
                <div class="product-story__copy">
                  <div class="product-story__meta">
                    <span>01</span>
                    <i />
                    <span>Chatland Support</span>
                  </div>
                  <div class="product-story__icon">
                    <MessageIcon />
                  </div>
                  <h3>
                    Twoja wiedza,
                    <br />
                    dostępna w rozmowie.
                  </h3>
                  <p>
                    Dla stron usługowych i informacyjnych. Chatland odpowiada
                    tak, jak przygotowany członek zespołu — zna zasady, potrafi
                    dopytać i nie zostawia klienta bez następnego kroku.
                  </p>
                  <ul>
                    {supportPoints.map((point) => (
                      <li key={point}>
                        <CheckIcon /> {point}
                      </li>
                    ))}
                  </ul>
                  <a class="product-story__link" href={contactHref}>
                    Zobacz zastosowanie Support <ArrowRightIcon />
                  </a>
                </div>

                <div
                  class="product-story__visual support-experience"
                  aria-hidden="true"
                >
                  <div class="experience-sitebar">
                    <strong>FORMA/</strong>
                    <span>Fizjoterapia</span>
                    <div>
                      <span>O nas</span>
                      <span>Oferta</span>
                      <span>Kontakt</span>
                    </div>
                  </div>
                  <div class="support-experience__copy">
                    <span>Ruch zaczyna się od dobrego planu</span>
                    <strong>Wróć do formy. Bez zgadywania.</strong>
                  </div>
                  <img src={supportArtwork} alt="" />
                  <div class="support-experience__availability">
                    <i />
                    <span>
                      Najbliższy termin
                      <strong>Jutro · 18:30</strong>
                    </span>
                  </div>
                  <div class="experience-dialogue experience-dialogue--support">
                    <div class="experience-dialogue__head">
                      <span class="experience-dialogue__avatar">ch</span>
                      <span>
                        <strong>Asystent FORMA</strong>
                        <small>Odpowiada na podstawie wiedzy firmy</small>
                      </span>
                    </div>
                    <p class="experience-dialogue__user">
                      Czy macie wolny termin po 18:00?
                    </p>
                    <div class="experience-dialogue__answer">
                      Tak — jutro o 18:30 jest wolna konsultacja. Mogę pomóc Ci
                      przygotować zgłoszenie.
                      <span>
                        <ShieldIcon /> Grafik wizyt · aktualny
                      </span>
                    </div>
                  </div>
                </div>
              </article>

              <article class="product-story product-story--commerce">
                <div class="product-story__copy">
                  <div class="product-story__meta">
                    <span>02</span>
                    <i />
                    <span>Chatland Commerce</span>
                  </div>
                  <div class="product-story__icon">
                    <SparkIcon />
                  </div>
                  <h3>
                    Od potrzeby
                    <br />
                    do właściwego produktu.
                  </h3>
                  <p>
                    Dla sklepów z małym i dużym katalogiem. Zamiast wyświetlać
                    przypadkową listę, Chatland rozumie zastosowanie i prowadzi
                    klienta przez kryteria, które naprawdę zmieniają decyzję.
                  </p>
                  <ul>
                    {commercePoints.map((point) => (
                      <li key={point}>
                        <CheckIcon /> {point}
                      </li>
                    ))}
                  </ul>
                  <a class="product-story__link" href={contactHref}>
                    Zobacz zastosowanie Commerce <ArrowRightIcon />
                  </a>
                </div>

                <div
                  class="product-story__visual commerce-experience"
                  aria-hidden="true"
                >
                  <div class="experience-sitebar experience-sitebar--dark">
                    <strong>PACEFORM</strong>
                    <span>Road collection</span>
                    <div>
                      <span>Buty</span>
                      <span>Technologie</span>
                      <span>O nas</span>
                    </div>
                  </div>
                  <div class="commerce-experience__copy">
                    <span>PACE / 001</span>
                    <strong>Zaprojektowane, żeby biec dalej.</strong>
                  </div>
                  <img src={commerceArtwork} alt="" />
                  <div class="commerce-experience__price">
                    <span>PACE 001</span>
                    <strong>359 zł</strong>
                  </div>
                  <div class="commerce-experience__variant">
                    <CheckIcon /> Rozmiar 42 dostępny
                  </div>
                  <div class="experience-dialogue experience-dialogue--commerce">
                    <div class="experience-dialogue__head">
                      <span class="experience-dialogue__avatar">ch</span>
                      <span>
                        <strong>Asystent PACEFORM</strong>
                        <small>Dobiera tylko potwierdzone warianty</small>
                      </span>
                    </div>
                    <p class="experience-dialogue__user">
                      Biegam po asfalcie. Rozmiar 42, do 450 zł.
                    </p>
                    <div class="experience-dialogue__answer">
                      PACE 001 spełnia wszystkie wymagania i jest dostępny w
                      rozmiarze 42.
                      <span>
                        <ShieldIcon /> 4/4 kryteriów potwierdzonych
                      </span>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section class="trust-section section" id="zaufanie">
          <div class="trust-orbit trust-orbit--one" aria-hidden="true" />
          <div class="trust-orbit trust-orbit--two" aria-hidden="true" />
          <div class="container trust-layout">
            <div class="trust-heading">
              <div class="trust-copy">
                <div class="eyebrow eyebrow--dark">
                  <ShieldIcon /> Chatland Trust Layer
                </div>
                <h2>
                  Pewność nie bierze się
                  <br />z tonu odpowiedzi.
                </h2>
              </div>
              <div class="trust-intro">
                <p>
                  Bierze się z procesu. Zanim Chatland odpowie, sprawdza źródło,
                  kontroluje krytyczne fakty i ocenia, czy rozmowa może pozostać
                  po stronie AI.
                </p>
                <a class="trust-link" href="#faq">
                  Poznaj zasady zaufania <ArrowRightIcon />
                </a>
              </div>
            </div>

            <div
              class="trust-proof"
              aria-label="Przykładowy przebieg kontroli odpowiedzi"
            >
              <div class="trust-proof__topbar">
                <div>
                  <i />
                  <span>Kontrola odpowiedzi</span>
                </div>
                <span>Trust trace / 00482</span>
              </div>
              <div class="trust-proof__question">
                <span>PYTANIE KLIENTA</span>
                <strong>
                  Czy mogę przełożyć wizytę bez dodatkowej opłaty?
                </strong>
              </div>
              <div class="trust-proof__flow">
                <div class="trust-proof__source">
                  <span class="trust-proof__icon">
                    <SearchIcon />
                  </span>
                  <div>
                    <small>01 · ŹRÓDŁO</small>
                    <strong>regulamin_wizyt.pdf</strong>
                    <span>Zweryfikowano · wersja 3.2</span>
                  </div>
                  <b>PEWNE</b>
                </div>
                <div class="trust-proof__source">
                  <span class="trust-proof__icon">
                    <ShieldIcon />
                  </span>
                  <div>
                    <small>02 · KONTROLA FAKTU</small>
                    <strong>Bezpłatnie do 12 h przed wizytą</strong>
                    <span>Warunek zgodny z aktualną polityką</span>
                  </div>
                  <b>ZGODNE</b>
                </div>
                <div class="trust-proof__source">
                  <span class="trust-proof__icon">
                    <HumanIcon />
                  </span>
                  <div>
                    <small>03 · DECYZJA</small>
                    <strong>Odpowiedź dozwolona</strong>
                    <span>Handoff pozostaje dostępny</span>
                  </div>
                  <b>94%</b>
                </div>
              </div>
              <div class="trust-proof__answer">
                <span class="trust-proof__check">
                  <CheckIcon />
                </span>
                <div>
                  <small>ODPOWIEDŹ OPARTA NA WIEDZY FIRMY</small>
                  <strong>
                    Tak. Wizytę możesz bezpłatnie przełożyć najpóźniej 12 godzin
                    przed jej rozpoczęciem.
                  </strong>
                </div>
                <span class="trust-proof__latency">1,2 s</span>
              </div>
            </div>

            <div class="trust-principles">
              {trustSteps.map((step) => (
                <article class="trust-principle" key={step.number}>
                  <div>
                    <span>{step.number}</span>
                    {step.icon}
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section class="workflow-section section" id="wdrozenie">
          <div class="container workflow-intro">
            <div>
              <p class="eyebrow">Od strony do pierwszej rozmowy</p>
              <h2>
                Trzy decyzje.
                <br />
                Żadnego projektu integracyjnego.
              </h2>
            </div>
            <div class="workflow-intro__aside">
              <span>Wdrożenie prowadzone wspólnie</span>
              <p>
                Nie zostawiamy pierwszych partnerów z dokumentacją i pustym
                panelem. Wspólnie porządkujemy wiedzę, zasady oraz moment
                startu.
              </p>
            </div>
          </div>

          <div class="container workflow-editorial">
            <div class="workflow-steps">
              {workflowStages.map((stage) => (
                <article key={stage.number}>
                  <div class="workflow-step__rail">
                    <span>{stage.number}</span>
                    <i />
                  </div>
                  <div class="workflow-step__body">
                    <div class="workflow-step__meta">
                      <span class="workflow-step__icon">{stage.icon}</span>
                      <b>{stage.phase}</b>
                    </div>
                    <h3>{stage.title}</h3>
                    <p>{stage.text}</p>
                    <small>
                      <CheckIcon /> {stage.proof}
                    </small>
                  </div>
                </article>
              ))}
            </div>

            <div
              class="launch-console"
              aria-label="Przykładowy stan uruchomienia Chatland"
            >
              <div class="launch-console__topbar">
                <div>
                  <i />
                  <i />
                  <i />
                </div>
                <span>chatland / publikacja</span>
                <b>Gotowe</b>
              </div>
              <div class="launch-console__heading">
                <div>
                  <span>FORMA / ASYSTENT</span>
                  <strong>Gotowy do pierwszej rozmowy.</strong>
                </div>
                <div class="launch-console__score">
                  <span>Stan wdrożenia</span>
                  <strong>100%</strong>
                </div>
              </div>
              <div class="launch-console__checks">
                <div>
                  <span class="launch-console__check">
                    <CheckIcon />
                  </span>
                  <p>
                    <small>ŹRÓDŁA WIEDZY</small>
                    <strong>24 strony · 6 dokumentów</strong>
                  </p>
                  <b>Aktualne</b>
                </div>
                <div>
                  <span class="launch-console__check">
                    <CheckIcon />
                  </span>
                  <p>
                    <small>ZASADY ODPOWIEDZI</small>
                    <strong>Trust Layer · polityka 1.4</strong>
                  </p>
                  <b>Zatwierdzone</b>
                </div>
                <div>
                  <span class="launch-console__check">
                    <CheckIcon />
                  </span>
                  <p>
                    <small>DOMENA</small>
                    <strong>forma.pl</strong>
                  </p>
                  <b>Zweryfikowana</b>
                </div>
              </div>
              <div class="launch-console__code">
                <span>&lt;script</span> src=
                <em>
                  &quot;https://api.chatland.pl/v1/widget/loader.js?site_key=••••••••&quot;
                </em>{" "}
                <span>async&gt;&lt;/script&gt;</span>
              </div>
              <div class="launch-console__live">
                <i />
                <span>
                  <strong>Asystent działa na 8 podstronach</strong>
                  Pierwsza rozmowa może rozpocząć się teraz
                </span>
                <ArrowUpRightIcon />
              </div>
            </div>
          </div>
        </section>

        <section class="pilot-section section">
          <div class="container pilot-card">
            <div class="pilot-card__pattern" aria-hidden="true" />
            <div class="pilot-card__topline">
              <span>Program pierwszych wdrożeń</span>
              <span>Edycja 01 · Polska</span>
            </div>
            <div class="pilot-card__copy">
              <p class="eyebrow eyebrow--light">Dla pierwszych partnerów</p>
              <h2>
                Nie szukamy logotypów.
                <br />
                Szukamy wspólnego wyniku.
              </h2>
              <p>
                Wspólnie wybieramy jeden realny problem klientów, uruchamiamy
                Chatland i przez pierwsze tygodnie mierzymy każdą ważną rozmowę.
                To ma być wdrożenie, nie prezentacja AI.
              </p>
              <div class="pilot-card__benefits">
                <span>
                  <b>01</b> Konfiguracja razem z zespołem Chatland
                </span>
                <span>
                  <b>02</b> Cotygodniowy przegląd rozmów i luk wiedzy
                </span>
                <span>
                  <b>03</b> Preferencyjne warunki pierwszego wdrożenia
                </span>
              </div>
            </div>
            <div class="pilot-card__side">
              <div class="pilot-card__mark" aria-hidden="true">
                <Mark />
              </div>
              <div class="pilot-card__availability">
                <i /> Nabór otwarty
              </div>
              <span>Najlepszy pierwszy przypadek</span>
              <strong>
                Powtarzalne pytania, których zespół nie powinien obsługiwać
                ręcznie.
              </strong>
              <p>
                Strona usługowa, informacyjna albo sklep. Branża jest mniej
                ważna niż problem, który da się uczciwie zmierzyć.
              </p>
              <a class="button button--paper button--large" href={contactHref}>
                Sprawdźmy dopasowanie <ArrowUpRightIcon />
              </a>
              <small>
                Krótka rozmowa · bez zobowiązań · odpowiadamy osobiście
              </small>
            </div>
          </div>
        </section>

        <section class="faq-section section" id="faq">
          <div class="container faq-intro">
            <div>
              <p class="eyebrow">Przed pierwszym wdrożeniem</p>
              <h2>
                Dobre pytania są częścią
                <br />
                dobrego produktu.
              </h2>
            </div>
            <p>
              AI na stronie firmy musi zasłużyć na zaufanie. Dlatego jasno
              opisujemy, skąd bierze odpowiedzi, czego nie zrobi samodzielnie i
              jak zachowuje się w sytuacji niepewności.
            </p>
          </div>

          <div class="container faq-layout">
            <aside class="faq-aside">
              <span>Nie chowamy trudnych tematów</span>
              <p>
                Nie znalazłeś odpowiedzi albo chcesz omówić nietypowy przypadek
                użycia?
              </p>
              <a href={contactHref}>
                Napisz bezpośrednio <ArrowRightIcon />
              </a>
            </aside>
            <div class="faq-list">
              {faqs.map((faq, index) => (
                <details
                  name="chatland-faq"
                  open={index === 0}
                  key={faq.question}
                >
                  <summary>
                    <span class="faq-list__number">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <strong>{faq.question}</strong>
                    <span class="faq-list__toggle">
                      <ChevronDownIcon />
                    </span>
                  </summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section class="final-cta section">
          <div
            class="final-cta__orbit final-cta__orbit--one"
            aria-hidden="true"
          />
          <div
            class="final-cta__orbit final-cta__orbit--two"
            aria-hidden="true"
          />
          <div class="container final-cta__inner">
            <div class="final-cta__index">
              <span>Twój następny krok</span>
              <b>01 / rozmowa</b>
            </div>
            <div class="final-cta__copy">
              <p class="eyebrow eyebrow--light">
                Każda rozmowa prowadzi dalej.
              </p>
              <h2>
                Zacznijmy od jednej,
                <br />
                która ma znaczenie.
              </h2>
              <p>
                Pokaż nam swoją stronę i najczęstszy problem klientów. Wrócimy z
                konkretnym pomysłem pierwszego wdrożenia.
              </p>
            </div>
            <div class="final-cta__action">
              <a class="button button--paper button--large" href={contactHref}>
                Porozmawiajmy o pilotażu <ArrowUpRightIcon />
              </a>
              <a href="mailto:kontakt@chatland.pl">kontakt@chatland.pl</a>
            </div>
          </div>
        </section>
      </main>

      <footer class="site-footer">
        <div class="container site-footer__statement">
          <div>
            <Brand inverted wordmarkOnly />
            <p>
              Inteligentna warstwa rozmowy dla stron, usług i sklepów.
              Projektowana w Polsce z ambicją działania na każdym rynku.
            </p>
          </div>
          <a class="site-footer__mail" href="mailto:kontakt@chatland.pl">
            <span>
              <small>Napisz bezpośrednio</small>
              kontakt@chatland.pl
            </span>
            <ArrowUpRightIcon />
          </a>
        </div>
        <div class="container site-footer__top">
          <div class="site-footer__manifesto">
            <span>CHATLAND / 2026</span>
            <p>
              Każda odpowiedź powinna prowadzić do lepszej decyzji — klienta i
              firmy.
            </p>
          </div>
          <div class="site-footer__links">
            <div>
              <strong>Produkt</strong>
              <a href="#produkt">Support</a>
              <a href="#produkt">Commerce</a>
              <a href="#zaufanie">Trust Layer</a>
            </div>
            <div>
              <strong>Firma</strong>
              <a href={contactHref}>Kontakt</a>
              <a href="https://demo.chatland.pl">Demo techniczne</a>
              <a href="https://panel.chatland.pl/login">Panel klienta</a>
            </div>
            <div>
              <strong>Informacje</strong>
              <a href="https://panel.chatland.pl/privacy">Prywatność</a>
              <a href="https://panel.chatland.pl/terms">Warunki korzystania</a>
            </div>
          </div>
        </div>
        <div class="container site-footer__bottom">
          <span>© {new Date().getFullYear()} Chatland</span>
          <span class="site-footer__status">
            <i /> Systemy działają prawidłowo
          </span>
          <span>Stworzone w Polsce dla rozmów na całym świecie.</span>
        </div>
      </footer>
    </div>
  );
}
